// API helpers for the impersonation surface (ADR-008).
//
// Two entry paths share this module:
//   - Path A: SPA lands at /impersonate?grant=<token> → exchangeGrant()
//   - Path B: SPA at /employee/login → startEmployeeLogin() → check-email
//             → /employee/login/confirm?token=<magic> → confirmEmployeeLogin()
//             → /employee/customers (search) → startImpersonation()

import type {
  CustomerUserDto,
  ImpersonationContextDto,
  InviteAccountResponseDto,
  InviteAccountResultDto,
  InviteCompanyResponseDto,
} from 'shared';

import { api, setAccessToken, setImpersonationMode } from './client.js';

// Re-exported so the existing importers in this package keep their import path.
// The shape itself is shared: the backend serializes it in one place
// (services/impersonation/context-dto.ts) and all three impersonation endpoints
// answer with it, so a field cannot be typed here and left unsent there.
export type { ImpersonationContextDto };

export type ImpersonationExchangeResponse = {
  access_token: string;
  access_token_expires_in: number;
  user: CustomerUserDto;
  impersonation: ImpersonationContextDto;
};

// Path A: exchange a Nexus-issued grant for an impersonation session.
export async function exchangeGrant(grant_token: string): Promise<ImpersonationExchangeResponse> {
  const res = await api.post<ImpersonationExchangeResponse>(
    '/auth/impersonation/exchange',
    { grant_token }
  );
  setAccessToken(res.data.access_token);
  setImpersonationMode(true);
  return res.data;
}

// End the active impersonation session. Returns 204.
export async function endImpersonation(): Promise<void> {
  try {
    await api.post('/auth/impersonation/end');
  } finally {
    setAccessToken(null);
    setImpersonationMode(false);
  }
}

// ── Path B (employee passwordless login) ────────────────────────────

export type EmployeeIdentityDto = {
  id: string;
  email: string;
};

let employeeAccessToken: string | null = null;

export function getEmployeeAccessToken(): string | null {
  return employeeAccessToken;
}

export function setEmployeeAccessToken(token: string | null): void {
  employeeAccessToken = token;
}

// POST /api/employee/login/start. Always 202 (anti-enumeration).
export async function startEmployeeLogin(email: string): Promise<void> {
  await api.post('/employee/login/start', { email });
}

// POST /api/employee/login/confirm. Returns the employee JWT + identity.
export async function confirmEmployeeLogin(token: string): Promise<{
  access_token: string;
  access_token_expires_in: number;
  employee: EmployeeIdentityDto;
}> {
  const res = await api.post<{
    access_token: string;
    access_token_expires_in: number;
    employee: EmployeeIdentityDto;
  }>('/employee/login/confirm', { token });
  setEmployeeAccessToken(res.data.access_token);
  return res.data;
}

// Mint an employee access token from the ACTIVE impersonation session
// (POST /api/auth/impersonation/employee-token). The default axios slot
// already holds the impersonation access token, so it's sent automatically —
// the backend reads the employee identity off the session row, not from us.
// This lets a rep who's already impersonating open the employee jobs grid
// without a separate magic-link sign-in (notably Path A / Nexus-initiated,
// which never planted an employee token in the SPA).
export async function acquireEmployeeTokenFromImpersonation(): Promise<EmployeeIdentityDto> {
  const res = await api.post<{
    access_token: string;
    access_token_expires_in: number;
    employee: EmployeeIdentityDto;
  }>('/auth/impersonation/employee-token');
  setEmployeeAccessToken(res.data.access_token);
  return res.data.employee;
}

export async function employeeLogout(): Promise<void> {
  try {
    await api.post(
      '/employee/logout',
      {},
      {
        headers: employeeAccessToken
          ? { Authorization: `Bearer ${employeeAccessToken}` }
          : {},
      }
    );
  } finally {
    setEmployeeAccessToken(null);
  }
}

export type PermittedCustomerDto = {
  customer_user_id: string;
  display_name: string | null;
  primary_email: string;
  // The Nexus Company this row is FOR. One account can appear on several rows,
  // one per company its address is a contact at, so this — not customer_user_id
  // — is what distinguishes them.
  company_id: string | null;
  company_name: string | null;
  // Portal-side invite state, joined on by the backend. `invite_locked` means
  // the account is active but has never had a password — the historical
  // migration's pre-approved-but-unreachable state — so the row offers an
  // invite. `last_invited_at` shows who has already been chased.
  invite_locked: boolean;
  last_invited_at: string | null;
};

// GET /api/employee/customers/search. Requires the employee JWT in the
// Authorization header (axios's default interceptor sends the customer
// access token; we override here).
export async function searchEmployeeCustomers(
  q: string,
  limit?: number
): Promise<PermittedCustomerDto[]> {
  const params: Record<string, string | number> = { q };
  if (typeof limit === 'number') params.limit = limit;
  const res = await api.get<{ results: PermittedCustomerDto[] }>(
    '/employee/customers/search',
    {
      params,
      headers: employeeAccessToken
        ? { Authorization: `Bearer ${employeeAccessToken}` }
        : {},
    }
  );
  return res.data.results;
}

// POST /api/employee/impersonation/start. Returns the impersonation
// session — same shape as Path A's exchange.
export async function startImpersonationAsEmployee(
  target_customer_user_id: string,
  // The company on the row the employee clicked. The picker lists one row per
  // (company, contact), so a contact address held at several companies appears
  // several times with the SAME customer_user_id — the id alone cannot say which
  // row was clicked, and the session would fall back to resolving the company
  // from the address and land on an arbitrary one.
  target_company_id?: string | null
): Promise<ImpersonationExchangeResponse> {
  const res = await api.post<ImpersonationExchangeResponse>(
    '/employee/impersonation/start',
    {
      target_customer_user_id,
      ...(target_company_id ? { target_company_id } : {}),
    },
    {
      headers: employeeAccessToken
        ? { Authorization: `Bearer ${employeeAccessToken}` }
        : {},
    }
  );
  // The impersonation access token replaces the employee token in the
  // axios default — from here on, /api/* customer routes use it. The
  // employee token stays in memory in case the SPA wants to switch
  // back without re-magic-linking.
  setAccessToken(res.data.access_token);
  setImpersonationMode(true);
  return res.data;
}

// POST /api/employee/customer-users/:id/invite — send one invite-locked
// account its set-password invite. 409 when the account already has a
// password, or when one was sent inside the resend cooldown.
export async function invitePortalAccount(
  customerUserId: string
): Promise<InviteAccountResultDto> {
  const res = await api.post<InviteAccountResponseDto>(
    `/employee/customer-users/${customerUserId}/invite`,
    {},
    {
      headers: employeeAccessToken
        ? { Authorization: `Bearer ${employeeAccessToken}` }
        : {},
    }
  );
  return res.data.result;
}

// POST /api/employee/companies/:company_id/invite — the same invite, swept
// across every invite-locked account at one company.
export async function inviteCompanyPortalAccounts(
  companyId: string
): Promise<InviteCompanyResponseDto> {
  const res = await api.post<InviteCompanyResponseDto>(
    `/employee/companies/${encodeURIComponent(companyId)}/invite`,
    {},
    {
      headers: employeeAccessToken
        ? { Authorization: `Bearer ${employeeAccessToken}` }
        : {},
    }
  );
  return res.data;
}
